import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, User, Copy, Check } from "lucide-react";
import Footer from "../components/Footer";
import { useApp } from "../context/AppContext";
import { api } from "../services/api";

export default function RewardsPage() {
  const navigate = useNavigate();
  const { user, restaurant } = useApp();

  useEffect(() => {
  if (restaurant && !restaurant.rewardsEnabled) {
    navigate("/", { replace: true });
  }
}, [restaurant, navigate]);

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [redeeming, setRedeeming] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [confirmingReward, setConfirmingReward] = useState(null);
  const [redeemError, setRedeemError] = useState(null);

  const inputsRef = useRef([]);
  const [publicRewards, setPublicRewards] = useState([]);
  const [copied, setCopied] = useState(false);
  const rewards = data?.rewards || publicRewards;
  const points = data?.customer?.points || 0;
 

    const sortedRewards = React.useMemo(() => {
  return [...rewards].sort((a, b) => a.pointsCost - b.pointsCost);
}, [rewards]);

        // Find the next reward the user hasn't unlocked yet
        const nextReward = sortedRewards.find(r => points < r.pointsCost);

        // Highest reward (for progress bar max)
        const maxPoints = sortedRewards.length
        ? sortedRewards[sortedRewards.length - 1].pointsCost
        : 10000;

        const remaining = nextReward
        ? nextReward.pointsCost - points
        : 0;

  const prevRewardPoints = sortedRewards
  .filter(r => r.pointsCost <= points)
  .pop()?.pointsCost || 0;

const nextRewardPoints = nextReward?.pointsCost || maxPoints;

const progress =
  nextRewardPoints === prevRewardPoints
    ? 100
    : Math.min(
        ((points - prevRewardPoints) /
          (nextRewardPoints - prevRewardPoints)) *
          100,
        100
      );
 

  const formatNumber = (num) =>
    new Intl.NumberFormat().format(num || 0);

  // OTP INPUT
  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleBackspace = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  // REQUEST OTP
  const requestOtp = async () => {
    setError(null);
    setLoading(true);

    try {
      await api.requestRewardsOtp(email);
      setStep("verify");
    } catch {
      setError("Failed to send code");
    }

    setLoading(false);
  };

  // VERIFY OTP
  const verifyOtp = async () => {
    setError(null);
    setLoading(true);

    try {
      const code = otp.join("");
      const res = await api.verifyRewardsOtp(email, code);

      setData(res);
      setStep("dashboard");
    } catch {
      setError("Invalid code");
    }

    setLoading(false);
  };

  // REDEEM REWARD
  const handleRedeem = async (reward) => {
    try {
      setRedeeming(reward.id);
      setRedeemError(null);

      const res = await api.redeemReward({
        rewardId: reward.id,
        email
      });

      setCoupon(res.code);
      setCopied(false);

      setData((prev) => ({
        ...prev,
        customer: {
          ...prev.customer,
          points: res.points
        }
      }));

    } catch (err) {
      setRedeemError("Failed to redeem reward");
    }

    setRedeeming(null);
    setConfirmingReward(null);
  };

  // LOAD PUBLIC REWARDS
  useEffect(() => {
    const loadRewards = async () => {
      try {
        const res = await api.getPublicRewards();
        setPublicRewards(res.rewards || []);
      } catch (err) {
        console.error("Failed to load rewards", err);
      }
    };

    loadRewards();
  }, []);

  if (restaurant && !restaurant.rewardsEnabled) return null;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* HEADER */}
      <div className="bg-amber-700 h-14 flex items-center px-6 justify-between">
        <div className="w-10"></div>

        <div className="flex items-center gap-3 ml-auto">
            <button className="flex items-center gap-1.5 text-white text-sm">
                <Gift className="w-4 h-4" />
                <span>Rewards</span>
            </button>

            <button
                onClick={() => navigate(user ? "/account" : "/login")}
                className="flex items-center gap-1.5 text-white text-sm"
            >
                <User className="w-4 h-4" />
                <span>
                {user ? user.name?.split(" ")[0] : "Account"}
                </span>
            </button>
            </div>
      </div>

      {/* HEADER PANEL */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-2">
            <div>
              <span className="text-3xl font-bold">
                {formatNumber(points)}
              </span>
              <span className="text-sm ml-1 text-gray-400">PTS</span>
            </div>

            <span className="text-sm text-gray-400">
              {nextReward
                    ? `${formatNumber(remaining)} pts to ${nextReward.name}`
                    : "All rewards unlocked 🎉"}
            </span>
          </div>

          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden mb-1">
            <div
              className="h-full bg-white transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex justify-between text-xs text-gray-500">
            <span>0</span>
            <span>{formatNumber(maxPoints)}</span>
          </div>

          {step !== "dashboard" && (
            <div className="mt-6">
              {error && (
                <div className="mb-3 p-3 rounded-lg bg-red-500 text-white text-sm">
                  {error}
                </div>
              )}

              {step === "email" && (
                <div className="flex bg-white rounded overflow-hidden">
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter Email"
                    className="flex-1 px-4 py-3 text-black text-sm outline-none"
                  />
                  <button
                    onClick={requestOtp}
                    disabled={loading}
                    className="bg-gray-900 text-white px-4"
                  >
                    →
                  </button>
                </div>
              )}

              {step === "verify" && (
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between gap-2">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputsRef.current[i] = el)}
                        value={digit}
                        onChange={(e) =>
                          handleOtpChange(e.target.value, i)
                        }
                        onKeyDown={(e) => handleBackspace(e, i)}
                        maxLength={1}
                        className="w-12 h-12 text-center text-lg font-bold rounded-lg text-black"
                      />
                    ))}
                  </div>

                  <button
                    onClick={verifyOtp}
                    disabled={loading}
                    className="bg-white text-black py-3 rounded-lg font-semibold"
                  >
                    Verify Code
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* MODAL */}
      {confirmingReward && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6">
            <h3 className="text-lg font-bold mb-2">
              Confirm Redemption
            </h3>

            <p className="text-sm text-gray-600">
              Redeem <strong>{confirmingReward.name}</strong> for{" "}
              {formatNumber(confirmingReward.pointsCost)} points?
            </p>

            <p className="text-xs text-gray-500 mt-2">
              You will have{" "}
              {formatNumber(points - confirmingReward.pointsCost)} points left
            </p>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setConfirmingReward(null)}
                className="flex-1 py-2 rounded-lg bg-gray-200 text-sm"
              >
                Cancel
              </button>

              <button
                onClick={() => handleRedeem(confirmingReward)}
                className="flex-1 py-2 rounded-lg bg-black text-white text-sm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REWARDS */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">
          Unlockable Rewards
        </h2>

        <p className="text-sm text-gray-500 mb-4">
          Earn points with every order and redeem rewards
        </p>

        {redeemError && (
          <div className="mb-4 p-3 bg-red-500 text-white rounded-lg text-sm">
            {redeemError}
          </div>
        )}

        {coupon && (
  <div className="mb-5 p-5 bg-white border border-gray-200 rounded-2xl shadow-sm">

    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
      Your Coupon
    </p>

    <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-xl border">

      <span className="text-lg font-bold tracking-widest text-gray-900">
        {coupon}
      </span>

      <button
        onClick={() => {
          navigator.clipboard.writeText(coupon);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="flex items-center gap-2 bg-black text-white px-3 py-2 rounded-lg text-sm hover:opacity-90 transition"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            Copied
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            Copy
          </>
        )}
      </button>
    </div>

    <p className="text-xs text-gray-400 mt-2">
      Use this code at checkout
    </p>
  </div>
)}

        <div className="space-y-3">
          {rewards.map((r) => {
            const canRedeem =
              step === "dashboard" && points >= r.pointsCost;

            return (
              <div
                key={r.id}
                className={`flex items-center justify-between p-4 rounded-xl border ${
                  canRedeem
                    ? "bg-white border-gray-200"
                    : "bg-gray-100 border-gray-100 opacity-70"
                }`}
              >
                <div>
                  <p className="font-semibold text-gray-900 text-sm">
                    {r.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {r.description}
                  </p>
                  <p className="text-sm font-bold mt-2">
                    {formatNumber(r.pointsCost)} points
                  </p>
                </div>

                <button
                  onClick={() => setConfirmingReward(r)}
                  disabled={!canRedeem || redeeming === r.id}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold ${
                    canRedeem
                      ? "bg-gray-900 text-white hover:bg-amber-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {redeeming === r.id ? "..." : "Redeem"}
                </button>
              </div>
            );
          })}
        </div>

        <button
          onClick={() => navigate("/")}
          className="w-full mt-6 bg-gray-200 text-black py-3 rounded-lg font-medium"
        >
          Back to Menu
        </button>
      </div>

      <Footer />
    </div>
  );
}